"use client";

import React from "react";
import { Phone, MapPin, Clock, ShieldCheck, Send, Loader as Loader2, CircleCheck as CheckCircle2, CircleAlert as AlertCircle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const serviceLocations = [{
  name: "Jonesboro (City Center)",
  zip: "30236",
  time: "15-30 min"
}, {
  name: "Riverdale",
  zip: "30274",
  time: "20-35 min"
}, {
  name: "Morrow",
  zip: "30260",
  time: "20-35 min"
}, {
  name: "Forest Park",
  zip: "30297",
  time: "25-40 min"
}, {
  name: "Stockbridge & Surrounds",
  zip: "30281",
  time: "25-45 min"
}, {
  name: "Lovejoy & South Clayton",
  zip: "30250",
  time: "20-35 min"
}, {
  name: "Rex",
  zip: "30273",
  time: "25-40 min"
}, {
  name: "Lake City",
  zip: "30260",
  time: "20-35 min"
}];
const issueTypeOptions = ["Burst Pipe", "No Water", "Sewage Backup", "Water Heater Failure", "Flooding", "Other"];
function EmergencyPlumbingForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-card rounded-xl border border-border">
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-4">
          <CheckCircle2 className="size-6 text-primary" />
        </div>
        <h3 className="font-default text-xl font-bold text-card-foreground">
          Request Received
        </h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-sm">
          {successMessage || "Our dispatch team has been notified. A licensed plumber will call your number immediately."}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      {/* Hidden Source Page Field */}
      <FormField control={control} name="source_page" render={({
      field
    }) => <input type="hidden" {...field} />} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Please enter your full name",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required for dispatch",
        pattern: {
          value: /^[0-9+\-()\s]{7,20}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(770) 555-0199" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField control={control} name="property_address" rules={{
        required: "Property address is required for dispatch"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Property Address</FormLabel>
              <FormControl>
                <Input placeholder="e.g. 124 Main St, Jonesboro, GA" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="issue_type" rules={{
        required: "Please select the emergency issue"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Emergency Issue</FormLabel>
              <FormControl>
                <select {...field} className="flex h-9 w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm text-foreground">
                  <option value="">Select an emergency issue...</option>
                  {issueTypeOptions.map(opt => <option key={opt} value={opt}>
                      {opt}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details (Optional)</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe what happened, where water is shutting off, or gate access codes..." className="min-h-20 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      validate: value => value === true || "You must consent to receive emergency dispatch communications"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-2.5 pt-1">
              <input type="checkbox" id="emergency-consent-checkbox" checked={Boolean(field.value)} onChange={e => field.onChange(e.target.checked)} className="size-4 mt-0.5 rounded border-input text-primary focus:ring-ring focus:ring-2 accent-primary cursor-pointer" />
              <FormLabel htmlFor="emergency-consent-checkbox" className="text-xs sm:text-sm text-muted-foreground font-normal leading-snug cursor-pointer">
                I agree to receive emergency dispatch calls or SMS regarding this service request.
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="flex items-center gap-2 p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
          <AlertCircle className="size-4 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full font-semibold shadow-sm">
        {isSubmitting ? <>
            <Loader2 className="animate-spin size-4" />
            Dispatching Request...
          </> : <>
            <Send className="size-4" />
            Dispatch On-Call Plumber
          </>}
      </Button>
    </Form>;
}
export default function ContactArea() {
  const shouldReduceMotion = useReducedMotion();
  const fadeIn = {
    initial: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.4
    }
  };
  return <section id="contactservicearea" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <motion.div className="text-center max-w-3xl mx-auto mb-12 md:mb-16" {...fadeIn}>
          <Badge variant="outline" className="mb-3 px-3 py-1 font-semibold text-primary border-primary/20 bg-primary/5">
            Local Emergency Response
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Fast Contact &amp; Service Coverage
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Directly serving Jonesboro, GA and surrounding Clayton County communities with 24/7 rapid plumbing response.
          </p>
        </motion.div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left: Contact Form Card */}
          <motion.div className="lg:col-span-7" {...fadeIn}>
            <Card className="shadow-md border-border">
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="font-default text-2xl font-bold">
                    Request Immediate Plumber
                  </CardTitle>
                  <Badge variant="secondary" className="font-medium flex items-center gap-1.5">
                    <span className="size-2 rounded-full bg-accent animate-pulse" />
                    Dispatch Open
                  </Badge>
                </div>
                <CardDescription>
                  Fill out this fast form or call our emergency line. Our local technicians are stationed across Clayton County.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="emergency_plumbing_dispatch_form" sectionName="contact-service-area" defaultValues={{
                full_name: "",
                phone: "",
                property_address: "",
                issue_type: "",
                additional_details: "",
                consent: false,
                source_page: "Fixture Problems - Emergency Dispatch"
              }} validationRules={{
                full_name: {
                  required: true,
                  minLength: 2
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                property_address: {
                  required: true
                },
                issue_type: {
                  required: true
                },
                consent: {
                  required: true
                }
              }} submitText="Dispatch On-Call Plumber" successMessage="Emergency dispatch request received. We are calling you immediately.">
                  <EmergencyPlumbingForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>

          {/* Right: Direct Info & Coverage Areas */}
          <motion.div className="lg:col-span-5 space-y-6" {...fadeIn}>
            {/* Direct Urgent Call Card */}
            <Card className="bg-primary text-primary-foreground border-transparent shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary-foreground/10 text-primary-foreground">
                    <Phone className="size-6" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                      Active Emergency Line
                    </p>
                    <h3 className="font-default text-2xl sm:text-3xl font-bold mt-1">
                      (470) 847-9076
                    </h3>
                    <p className="text-sm text-primary-foreground/80 mt-1">
                      Call now for active leaks, sewer backups, or loss of water.
                    </p>
                    <div className="mt-4">
                      <Button asChild variant="secondary" size="default" className="font-semibold text-secondary-foreground shadow-sm w-full sm:w-auto">
                        <a href="tel:4708479076">
                          <Phone className="size-4" />
                          Call Dispatch Directly
                        </a>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Area Grid Card */}
            <Card className="border-border shadow-sm">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="size-5 text-primary" />
                  <CardTitle className="font-default text-lg font-bold">
                    Clayton County Service Areas
                  </CardTitle>
                </div>
                <CardDescription>
                  Standard emergency arrival windows under normal traffic conditions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {serviceLocations.map((loc, i) => <div key={loc.name} data-index={i} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/60 border border-border/50 text-xs sm:text-sm">
                      <span className="font-medium text-foreground">{loc.name}</span>
                      <span className="text-muted-foreground font-mono text-xs">{loc.time}</span>
                    </div>)}
                </div>

                <Separator className="my-4" />

                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="size-3.5 text-primary" />
                    24 Hours / 7 Days a Week
                  </span>
                  <span className="flex items-center gap-1.5">
                    <ShieldCheck className="size-3.5 text-primary" />
                    Licensed &amp; Insured
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}